import { Button, dialog } from "nq-component";
import BaseListPresenter from "../../../base/BaseListPresenter";
import * as XLSX from "xlsx";
import { findObjectUseCase, upsertUseCase } from "../../../usecases/object";
import AggregateUseCase from "../../../usecases/object/AggregateUseCase";
import {
  getPipelineHolidays,
  getPipelineSuspension,
} from "../../../components/common/PipelineQuery";
import {
  minuteConverterToCredit,
  formatDate,
} from "../../../components/common/Converter";
import {
  getScheduleForDay,
  getEffectiveScheduleAssignmentsForMonth,
} from "./ScheduleSequences";
import { applyLateUndertimeCreditDeduction } from "./LateUndertimeDeduction";

const toDateKey = (value) => {
  if (!value) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  return date.toISOString().split("T")[0];
};

const leaveCoversDate = (ranges, dateKey) => {
  const flat = (ranges ?? []).flat(Infinity);
  for (let i = 0; i + 1 < flat.length; i += 2) {
    const from = toDateKey(flat[i]);
    const to = toDateKey(flat[i + 1]);
    if (from && to && dateKey >= from && dateKey <= to) return true;
  }
  return false;
};

/** Minutes from midnight; null when missing/invalid. Matches BiometricDTR. */
const parseTime = (t) => {
  if (!t || t === "--:--") return null;
  const [h = 0, m = 0] = String(t).split(":").map(Number);
  const v = h * 60 + m;
  return Number.isNaN(v) ? null : v;
};

/** Resolve schedule time fields (timein/timeIn, timeout/timeOut, etc.). */
const scheduleTime = (day, key) => {
  if (!day) return null;
  const camel =
    key === "timein" ? "timeIn" : key === "timeout" ? "timeOut" : key;
  const v = day[key] ?? day[camel];
  if (v != null && v !== "") return v;
  const lower = key.toLowerCase();
  return key !== lower ? day[lower] : null;
};

const isGraveyardDay = (day, sched) =>
  !!day &&
  (day.day?.includes(" - ") ||
    day.day?.includes("Graveyard") ||
    /Graveyard/i.test(sched?.selectedOption ?? ""));

class ManualTSheetsPresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    exportCSVUseCase,
    addSchemaUseCase,
    updateSchemaUseCase,
    deleteSchemaUseCase,
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.exportCSVUseCase = exportCSVUseCase;
    this.addSchemaUseCase = addSchemaUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.deleteSchemaUseCase = deleteSchemaUseCase;
    this.view = view;
  }

  init() {
    this.where = {};
    this.search = {};
    this.filter = {};
    this.include = [];
    this.keys = [
      "Firstname",
      "Middlename",
      "surname",
      "selectedSchedule",
      "region",
      "office",
      "division",
      "section",
      "unit",
      "cluster",
      "twentyFourSeven",
      "flexibleSchedule",
      "biometricId",
    ];
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  async findObjects() {
    const collection = this.view.getCollectionName();
    const limit = 1000;
    let skip = 0;
    let hasMore = true;
    try {
      this.showProgress();
      this.findObjectUseCase.abort();
      while (hasMore) {
        const objects = await this.findObjectUseCase.execute(collection, {
          where: { ...this.where },
          limit,
          include: this.include,
          keys: this.keys,
          skip,
        });
        if (!objects || objects.length === 0) {
          hasMore = false;
        }

        this.view.setState((prevState) => ({
          employees: [...objects, ...(prevState.employees || [])],
        }));

        skip += limit;
      }
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
    this.progress = false;
  }

  parseExcelTime(timeFraction) {
    if (
      typeof timeFraction !== "number" ||
      timeFraction < 0 ||
      timeFraction >= 1
    ) {
      return "--:--";
    }

    const totalSeconds = Math.round(timeFraction * 24 * 60 * 60);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;

    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }

  parseExcelDateForInput(serial) {
    if (typeof serial !== "number" || isNaN(serial) || serial <= 0) {
      return "";
    }

    const utcDays = Math.floor(serial - 25569);
    const utcValue = utcDays * 86400;
    const date = new Date(utcValue * 1000);

    if (isNaN(date.getTime())) {
      return "";
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  /**
   * Fetch holidays, suspensions, existing biometric logs, and leave requests
   * once for the whole Excel date range (4 requests total instead of 4 per row).
   */
  async prefetchImportData(employee, dateStrs) {
    const uniqueDates = [...new Set(dateStrs.filter(Boolean))].sort();
    if (uniqueDates.length === 0) {
      return {
        holidays: [],
        suspensions: [],
        biometricLogs: [],
        leaveRequests: [],
      };
    }

    const minDate = uniqueDates[0];
    const maxDate = uniqueDates[uniqueDates.length - 1];
    const pin = String(employee?.biometricId ?? employee?.id);
    const startOfRange = new Date(`${minDate}T00:00:00.000Z`);
    const endOfRange = new Date(`${maxDate}T23:59:59.999Z`);
    const aggregateUseCase = new AggregateUseCase();

    const holidayPipeline = getPipelineHolidays(employee);
    holidayPipeline[0].$match.$expr.$and.push({
      $gte: ["$date", { $dateFromString: { dateString: minDate } }],
    });
    holidayPipeline[0].$match.$expr.$and.push({
      $lte: ["$date", { $dateFromString: { dateString: maxDate } }],
    });

    const suspensionPipeline = getPipelineSuspension(employee);
    // Overlap: suspension.from <= maxDate AND suspension.to >= minDate
    suspensionPipeline[0].$match.$expr.$and.push({
      $lte: ["$from", { $dateFromString: { dateString: maxDate } }],
    });
    suspensionPipeline[0].$match.$expr.$and.push({
      $gte: ["$to", { $dateFromString: { dateString: minDate } }],
    });

    const [holidays, suspensions, biometricLogs, leaveRequests] =
      await Promise.all([
        aggregateUseCase.execute("holidays", holidayPipeline),
        aggregateUseCase.execute("suspension", suspensionPipeline),
        findObjectUseCase().execute("biometric_logs", {
          where: {
            pin,
            timestamp: { $gte: startOfRange, $lte: endOfRange },
          },
          keys: ["timestamp"],
          limit: 5000,
        }),
        findObjectUseCase().execute("leave_request", {
          where: {
            stats: { $eq: "Approved" },
            "employee.id": employee?.id,
            dateRange: {
              $elemMatch: {
                $elemMatch: {
                  0: { $lte: maxDate },
                  1: { $gte: minDate },
                },
              },
            },
          },
          keys: ["selectedType", "dateRange"],
        }),
      ]);

    return {
      holidays: Array.isArray(holidays) ? holidays : [],
      suspensions: Array.isArray(suspensions) ? suspensions : [],
      biometricLogs: Array.isArray(biometricLogs) ? biometricLogs : [],
      leaveRequests: Array.isArray(leaveRequests) ? leaveRequests : [],
    };
  }

  getDayLookup(prefetched, dateStr) {
    const holidays = prefetched.holidays.filter(
      (h) => toDateKey(h.date) === dateStr,
    );
    const suspensions = prefetched.suspensions.filter((s) => {
      const from = toDateKey(s.from);
      const to = toDateKey(s.to);
      return from && to && dateStr >= from && dateStr <= to;
    });
    const timeSheetRecord = prefetched.biometricLogs.filter(
      (log) => toDateKey(log.timestamp) === dateStr,
    );
    const leaveRequests = prefetched.leaveRequests.filter((lr) =>
      leaveCoversDate(lr?.dateRange, dateStr),
    );

    return [holidays, suspensions, timeSheetRecord, leaveRequests];
  }

  handleExcelUpload = (event) => {
    const file = event.target.files[0];

    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      this.view.setState({ rawExcelData: jsonData });
    };

    reader.readAsArrayBuffer(file);
  };

  processRow(
    row,
    index,
    employee,
    rawExcelData,
    effectiveScheduleAssignments,
    employeeSchedule,
    prefetched,
  ) {
    const dateStr = this.parseExcelDateForInput(row.date);
    if (!dateStr) return null;

    // Noon UTC avoids weekday shifting across timezones (same as BiometricDTR)
    const date = new Date(`${dateStr}T12:00:00.000Z`);
    const dateKey = dateStr;

    const effectiveSchedule =
      effectiveScheduleAssignments.find(
        (a) =>
          a?.created &&
          toDateKey(a.created) &&
          toDateKey(a.created) <= dateKey,
      ) ?? null;

    const scheduleToUse = effectiveSchedule || employeeSchedule;
    const scheduleForDay = getScheduleForDay(scheduleToUse, date);

    const timeInStr = this.parseExcelTime(row.timeIn);
    const timeOutStr = this.parseExcelTime(row.timeOut);
    const timeAmOutStr = this.parseExcelTime(row.timeAmOut);
    const timePmInStr = this.parseExcelTime(row.timePmIn);

    let lateMinutes = 0;
    let undertimeMinutes = 0;

    const [holidays, suspensions, timeSheetRecord, leaveRequests] =
      this.getDayLookup(prefetched, dateStr);

    if (timeSheetRecord.length > 0) {
      this.view.showError(
        `Oops! The date ${formatDate(
          timeSheetRecord[0].timestamp ?? row.date,
        )} has already been recorded for the selected employee. Skipping.`,
      );
      return null;
    }

    const isNineHours = !["9 Hours", "12 Hours"].includes(
      employee?.twentyFourSeven,
    );

    const skipLateUndertime =
      leaveRequests.length > 0 ||
      (isNineHours && (holidays.length > 0 || suspensions.length > 0));

    if (scheduleForDay && !skipLateUndertime) {
      const isGraveyard = isGraveyardDay(scheduleForDay, scheduleToUse);
      const weekday = date.toLocaleDateString("en-US", {
        weekday: "long",
        timeZone: "UTC",
      });

      // "Mon - Tue" matches both days; attribute late/UT only to the start day
      let isSpanEndOnly = false;
      if (scheduleForDay.day?.includes(" - ")) {
        const [startDay, endDay] = scheduleForDay.day
          .split(" - ")
          .map((s) => s.trim());
        isSpanEndOnly = weekday === endDay && weekday !== startDay;
      }

      if (!isSpanEndOnly) {
        // Same punch selection as BiometricDTR
        let tin = isGraveyard
          ? timePmInStr !== "--:--"
            ? timePmInStr
            : timeInStr
          : timeInStr !== "--:--"
            ? timeInStr
            : timePmInStr;
        let tout = isGraveyard
          ? timeAmOutStr !== "--:--"
            ? timeAmOutStr
            : timeOutStr
          : timeOutStr !== "--:--"
            ? timeOutStr
            : timeAmOutStr;

        // Graveyard shift may clock out next morning — use next Excel row's AM out
        if (isGraveyard && (!tout || tout === "--:--")) {
          const nextRow = rawExcelData[index + 1];
          if (nextRow) {
            const nextAmOut = this.parseExcelTime(nextRow.timeAmOut);
            if (nextAmOut && nextAmOut !== "--:--") tout = nextAmOut;
          }
        }

        const sIn = parseTime(
          scheduleTime(scheduleForDay, "lateTimeIn") ||
            scheduleTime(scheduleForDay, "timein"),
        );
        const sOut = parseTime(
          scheduleTime(scheduleForDay, "earliestTimeOut") ||
            scheduleTime(scheduleForDay, "timeout"),
        );
        const tinM = parseTime(tin);
        const toutM = parseTime(tout);

        // Cross-midnight: schedule out is next calendar morning
        let sOutForUT = sOut;
        let toutMForUT = toutM;
        if (isGraveyard && sIn != null && sOut != null && sOut < sIn) {
          sOutForUT = sOut + 24 * 60;
          if (toutM != null && toutM < sIn) {
            toutMForUT = toutM + 24 * 60;
          }
        }

        if (tinM != null && sIn != null && tinM > sIn) {
          lateMinutes = tinM - sIn;
        }
        if (
          toutMForUT != null &&
          sOutForUT != null &&
          toutMForUT < sOutForUT
        ) {
          undertimeMinutes = sOutForUT - toutMForUT;
        }
      }
    }

    const biometricRecords = [];
    const addPunch = (timeVal, status) => {
      const timeStr = this.parseExcelTime(timeVal);
      if (timeStr && timeStr !== "--:--") {
        biometricRecords.push({
          pin: String(employee.biometricId),
          origin: "DTR",
          timestamp: `${dateStr}T${timeStr}.000Z`,
          status,
        });
      }
    };
    addPunch(row.timeIn, 0);
    addPunch(row.timeOut, 1);
    addPunch(row.timeAmOut, 2);
    addPunch(row.timePmIn, 3);

    if (biometricRecords.length === 0) return null;

    return {
      dateStr,
      biometricRecords,
      lateMinutes: minuteConverterToCredit(lateMinutes),
      undertimeMinutes: minuteConverterToCredit(undertimeMinutes),
    };
  }

  /**
   * Deduct late/undertime leave credits only for last-month dates,
   * and mirror each SL/VL deduction onto leave_card.
   */
  async applyLateUndertimeDeductions(employee, listOfLeave, item) {
    const result = applyLateUndertimeCreditDeduction({
      employeeId: employee.id,
      dateStr: item?.dateStr,
      lateMinutes: item?.lateMinutes,
      undertimeMinutes: item?.undertimeMinutes,
      listOfLeave,
    });
    if (!result.applied) return;

    for (const credit of result.updatedCredits) {
      await upsertUseCase().execute("leave_cred", credit);
    }
    for (const card of result.leaveCards) {
      await upsertUseCase().execute("leave_card", card);
    }
  }

  async confirmManualTimeSheet() {
    dialog.close();
    this.view.setState({ isSubmitted: true });
    try {
      const { rawExcelData, employee } = this.view.state;
      const employeeSchedule = employee?.selectedSchedule;

      if (!rawExcelData || rawExcelData.length === 0) return;

      if (employee?.flexibleSchedule) {
        try {
          await this.handleFlextibleEmployeeSchedule(employee, rawExcelData);
        } catch (e) {
          console.error(e.message || e);
        }
        return;
      }

      const dateStrs = rawExcelData.map((row) =>
        this.parseExcelDateForInput(row.date),
      );
      const dates = dateStrs
        .filter(Boolean)
        .map((dateStr) => new Date(dateStr));
      const maxDate =
        dates.length > 0
          ? new Date(Math.max(...dates.map((d) => d.getTime())))
          : null;
      const endOfRangeIso = maxDate
        ? new Date(
            maxDate.getFullYear(),
            maxDate.getMonth(),
            maxDate.getDate(),
            23,
            59,
            59,
            999,
          ).toISOString()
        : null;

      const [effectiveScheduleAssignments, prefetched] = await Promise.all([
        getEffectiveScheduleAssignmentsForMonth(employee?.id, endOfRangeIso),
        this.prefetchImportData(employee, dateStrs),
      ]);

      // Pure in-memory processing — no per-row API calls
      const enrichedData = rawExcelData.map((row, index) =>
        this.processRow(
          row,
          index,
          employee,
          rawExcelData,
          effectiveScheduleAssignments,
          employeeSchedule,
          prefetched,
        ),
      );

      const finalData = enrichedData.filter((entry) => entry !== null);
      const listOfLeave = await findObjectUseCase().execute("leave_cred", {
        where: {
          empID: employee.id,
          type: { $in: ["Vacation Leave", "Sick Leave"] },
        },
      });

      for (const item of finalData) {
        for (const record of item.biometricRecords) {
          await upsertUseCase().execute("biometric_logs", record);
        }

        if (
          employee?.selectedSchedule?.scheduleArray?.length > 0 &&
          listOfLeave.length > 0
        ) {
          await this.applyLateUndertimeDeductions(employee, listOfLeave, item);
        }
      }

      this.view.showSuccess("Data Successfully imported");
    } catch (e) {
      console.error("Error", e);
    } finally {
      this.view.setState({ employee: { name: "" }, isSubmitted: false });
      this.view.fileInputRef.current.value = "";
    }
  }

  handleSubmitBtn() {
    dialog.fire({
      html: (
        <div className="p-2">
          <div className="row">
            <h4>Confirmation</h4>
            <hr />
            <p style={{ fontSize: "13px", padding: "1rem" }}>
              Are you sure you want to import this data?
            </p>
            <hr />
          </div>
          <div className="w-100 d-flex justify-content-end gap-4 pe-3 pb-3">
            <Button
              className="btn btn-light fs-sm"
              onClick={() => dialog.close()}
            >
              Cancel
            </Button>
            <Button
              className="btn btn-primary"
              onClick={() => this.confirmManualTimeSheet()}
            >
              Confirm
            </Button>
          </div>
        </div>
      ),
      footer: false,
    });
  }

  // .dat file
  async fileReader(event) {
    const file = event.target.files[0];
    if (!file) return;

    const employees = await findObjectUseCase().execute("employees", {
      keys: ["id", "selectedSchedule", "twentyFourSeven", "biometricId"],
      include: ["selectedSchedule"],
    });

    const reader = new FileReader();

    reader.onload = (e) => {
      const contents = e.target.result;
      const lines = contents.trim().split("\n");
      const status = {
        0: "timeIn",
        1: "timeOut",
        2: "timePmIn",
        3: "timeAmOut",
      };

      const parsedData = lines.map((line) => {
        const [id, date, time, mode, stats, verify, workCode] = line
          .trim()
          .split(/\s+/);

        const employee = employees.find(
          (emp) => emp.biometricId === Number(id),
        );
        if (!employee) {
          console.error(`Employee with biometricId ${id} not found.`);
          return null;
        }

        return {
          date: date,
          [status[stats]]: time,
          employee: { id: employee.id },
          twentyFourSeven: employee.twentyFourSeven,
          schedule: employee.selectedSchedule,
        };
      });

      console.log(parsedData);
    };

    reader.readAsText(file, "UTF-8");
  }

  async handleFlextibleEmployeeSchedule(employee, rawExcelData) {
    const employeeSchedule = employee?.selectedSchedule;

    // Process rows sequentially to avoid rate limiting
    const enrichedData = [];
    for (const row of rawExcelData) {
      const actualTimeAmIn = new Date(
        `${this.parseExcelDateForInput(row.date)}T${this.parseExcelTime(row.timeIn)}`,
      );
      const actualTimePmOut = new Date(
        `${this.parseExcelDateForInput(row.date)}T${this.parseExcelTime(row.timeOut)}`,
      );
      const timeAmOut = new Date(
        `${this.parseExcelDateForInput(row.date)}T${this.parseExcelTime(row.timeAmOut)}`,
      );
      const timePmIn = new Date(
        `${this.parseExcelDateForInput(row.date)}T${this.parseExcelTime(row.timePmIn)}`,
      );

      let dailyDurationMs = 0;
      if (actualTimeAmIn && actualTimePmOut) {
        dailyDurationMs = actualTimePmOut - actualTimeAmIn;
      } else if (timePmIn && timeAmOut) {
        if (timeAmOut < timePmIn) {
          const nextDayTimeAmOut = new Date(timeAmOut);
          nextDayTimeAmOut.setDate(nextDayTimeAmOut.getDate() + 1);
          dailyDurationMs = nextDayTimeAmOut - timePmIn;
        } else {
          dailyDurationMs = timeAmOut - timePmIn;
        }
      }

      const dateStr = this.parseExcelDateForInput(row.date);
      const biometricRecords = [];
      const addPunch = (timeVal, status) => {
        const timeStr = this.parseExcelTime(timeVal);
        if (timeStr && timeStr !== "--:--") {
          biometricRecords.push({
            pin: String(employee.biometricId ?? employee.id),
            timestamp: `${dateStr}T${timeStr}.000Z`,
            status,
          });
        }
      };
      addPunch(row.timeIn, 0);
      addPunch(row.timeOut, 1);
      addPunch(row.timeAmOut, 2);
      addPunch(row.timePmIn, 3);

      if (biometricRecords.length === 0) continue;

      enrichedData.push({
        dateStr,
        biometricRecords,
        lateMinutes: minuteConverterToCredit(0),
        undertimeMinutes: minuteConverterToCredit(0),
        _dailyDurationMs: dailyDurationMs,
      });
    }

    const validData = enrichedData.filter((record) => record !== null);
    const totalMonthlyDurationMs = validData.reduce(
      (total, record) => total + (record._dailyDurationMs || 0),
      0,
    );
    const hours = Math.floor(totalMonthlyDurationMs / (1000 * 60 * 60));

    if (180 > hours) {
      this.view.setState({ employee: { name: "" }, isSubmitted: false });
      dialog.close();
      this.view.showError(
        "Monthly hours requirement (180h) not met for flexible schedule.",
      );
      this.view.fileInputRef.current.value = "";
      return;
    }

    const listOfLeave = await findObjectUseCase().execute("leave_cred", {
      where: {
        empID: employee.id,
        type: { $in: ["Vacation Leave", "Sick Leave"] },
      },
    });

    for (const item of validData) {
      delete item._dailyDurationMs;
      for (const record of item.biometricRecords) {
        await upsertUseCase().execute("biometric_logs", record);
      }
      if (
        employeeSchedule?.scheduleArray?.length > 0 &&
        listOfLeave.length > 0
      ) {
        await this.applyLateUndertimeDeductions(employee, listOfLeave, item);
      }
    }

    this.view.setState({ employee: { name: "" }, isSubmitted: false });
    dialog.close();
    this.view.showSuccess("Data Successfully imported");
    this.view.fileInputRef.current.value = "";
  }
}
export default ManualTSheetsPresenter;
